import { calculatePercentChange } from 'src/utils/app';
import {
  ContentMetricType,
  ContentScores,
  ContentAccuracyScores,
  MetaDataMetrics,
  AccuracyMetaDataMetrics
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import {
  ChartDataAdapter,
  KeyMetric,
  KeyMetricScoreTitle,
  KeyMetricTitle
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';

export interface KeyMetricsData {
  primaryOverallScore: number;
  secondaryOverallScore?: number;
  primaryData: KeyMetric[];
  secondaryData?: Omit<KeyMetric, 'change'>[];
  topEntityPrimaryData: [string, number][];
  topEntitySecondaryData: [string, number][];
  rawData: {
    mainKeyMetrics: ContentScoresWithMetadata;
    comparisonKeyMetrics: ContentScoresWithMetadata;
  };
}

export interface AccuracyKeyMetricsData {
  primaryOverallScore: number;
  secondaryOverallScore?: number;
  primaryData: KeyMetric[];
  secondaryData?: Omit<KeyMetric, 'change'>[];
  topEntityPrimaryData: [string, number][];
  topEntitySecondaryData: [string, number][];
  rawData: {
    mainKeyMetrics: AccuracyContentScoresWithMetadata;
    comparisonKeyMetrics: AccuracyContentScoresWithMetadata;
  };
}

const CONTENT_TYPE_TO_OVERALL_SCORE = {
  [ContentMetricType.ContentScore]: 'contentScore',
  [ContentMetricType.ContentUtilizationScore]: 'contentUtilizationScore',
  [ContentMetricType.ContentAccuracyScore]: 'contentAccuracyScore'
};

export type ContentScoresWithMetadata = ContentScores & {
  metadataMetrics?: MetaDataMetrics;
};

export type AccuracyContentScoresWithMetadata = ContentAccuracyScores & {
  metadataMetrics?: AccuracyMetaDataMetrics;
};

export class KeyMetricsDataAdapter implements ChartDataAdapter<KeyMetricsData> {
  mainKeyMetrics: ContentScoresWithMetadata;

  comparisonKeyMetrics: ContentScoresWithMetadata;

  metricType: ContentMetricType;

  constructor(
    mainKeyMetrics: ContentScoresWithMetadata,
    comparisonKeyMetrics: ContentScoresWithMetadata,
    metricType: ContentMetricType
  ) {
    this.mainKeyMetrics = mainKeyMetrics;
    this.comparisonKeyMetrics = comparisonKeyMetrics;
    this.metricType = metricType;
  }

  transform(): KeyMetricsData {
    const getChange = (metricName: string) =>
      calculatePercentChange(Number(this.mainKeyMetrics[metricName]), Number(this.comparisonKeyMetrics[metricName]));

    const primaryData = Object.entries(this.mainKeyMetrics).map(([metricName, value]) => ({
      id: metricName,
      name: KeyMetricTitle[metricName],
      value: Number(value),
      change: getChange(metricName)
    }));

    const secondaryData = Object.entries(this.comparisonKeyMetrics).map(([metricName, value]) => ({
      id: metricName,
      name: KeyMetricTitle[metricName],
      value: Number(value)
    }));

    const topEntityPrimaryData = Object.entries(this.mainKeyMetrics).map(([metricName, value]) => [
      KeyMetricScoreTitle[metricName],
      Number(value)
    ]) as [string, number][];

    const topEntitySecondaryData = Object.entries(this.comparisonKeyMetrics).map(([metricName, value]) => [
      KeyMetricScoreTitle[metricName],
      Number(value)
    ]) as [string, number][];

    const scoreKey = CONTENT_TYPE_TO_OVERALL_SCORE[this.metricType];
    const primaryOverallScore = primaryData.find((metric) => metric.id === scoreKey)?.value ?? 0;
    const secondaryOverallScore = secondaryData.find((metric) => metric.id === scoreKey)?.value;

    return {
      primaryOverallScore,
      secondaryOverallScore,
      primaryData,
      secondaryData,
      topEntityPrimaryData,
      topEntitySecondaryData,
      rawData: {
        mainKeyMetrics: this.mainKeyMetrics,
        comparisonKeyMetrics: this.comparisonKeyMetrics
      }
    };
  }
}

export class KeyMetricsFilterAdapter implements ChartDataAdapter<KeyMetricsData> {
  keyMetricsDataAdapter: KeyMetricsDataAdapter;

  keyMetricsMap: Record<string, string>;

  constructor(keyMetricsDataAdapter: KeyMetricsDataAdapter, keyMetricsMap: Record<string, string>) {
    this.keyMetricsDataAdapter = keyMetricsDataAdapter;
    this.keyMetricsMap = keyMetricsMap;
  }

  transform(): KeyMetricsData {
    const {
      primaryOverallScore,
      secondaryOverallScore,
      primaryData,
      secondaryData,
      topEntityPrimaryData,
      topEntitySecondaryData,
      rawData
    } = this.keyMetricsDataAdapter.transform();
    const keys = Object.keys(this.keyMetricsMap);

    const filterMetric = (metric: KeyMetric) => keys.includes(metric.id);

    const substituteMetricName = (metric: KeyMetric) => ({
      ...metric,
      name: this.keyMetricsMap[metric.id] || metric.name
    });

    return {
      topEntityPrimaryData,
      topEntitySecondaryData,
      primaryOverallScore,
      secondaryOverallScore,
      primaryData: primaryData.filter(filterMetric).map(substituteMetricName),
      secondaryData: secondaryData.filter(filterMetric).map(substituteMetricName),
      rawData
    };
  }
}
